import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useMemo } from "react";

import { useSelectedTimeRange } from "@/features/dashboard/store";
import { BLUE, colorGenerator, GREEN } from "@/features/shared/widgets/graphs/helpers";
import { ParameterTimeSeriesChartResult } from "@/lib/api";
import { renderValue } from "@/features/dashboard/components/timeseries/render-value";
import { DimensionValue } from "@/lib/dimensions";
import { getYAxisPerUnit } from "@/components/graph/get-y-axis-for-unit";
import { NoDataOverlay } from "@/features/shared/widgets/graphs/no-data-label";
import {
  chartSeriesIsDisabled,
  chartSeriesPresentationColor,
  formatChartSeriesName,
  getChartSeriesPresentationState,
  resolveSeriesDisplayLabel,
} from "@/components/graph/chart-series-presentation";
import { useTranslation } from "@/lib/i18n";

import useGraphOptions from "./use-graph-options";
import { resolveGraphChartHeight } from "./resolve-graph-chart-height";
import { useTimeSeriesNoData } from "./hooks/use-time-series-no-data";
import type { ChartTimeBounds } from "./get-shared-chart-time-bounds";
import { HistogramTimeSeriesConfig } from "./graph.types";
import { buildThresholdPlotBands, buildThresholdPlotLines } from "./thresholds";

interface HistogramProps {
  config: HistogramTimeSeriesConfig;
  data: ParameterTimeSeriesChartResult[];
  currentTime: DimensionValue;
  timeBounds?: ChartTimeBounds;
  height?: number;
}

const Histogram = ({ config, data, currentTime, timeBounds, height }: HistogramProps) => {
  const { t } = useTranslation();
  const timeRange = useSelectedTimeRange();

  const seriesWithData = useMemo<Highcharts.SeriesOptionsType[]>(() => {
    const greenColorGenerator = colorGenerator(GREEN);
    const blueColorGenerator = colorGenerator(BLUE);
    const getColor = (color: string) => {
      const fallback = "grey";
      if (color === "green") {
        return greenColorGenerator.next().value ?? fallback;
      }
      if (color === "blue") {
        return blueColorGenerator.next().value ?? fallback;
      }
      return fallback;
    };

    return config.series.map((seriesConfig) => {
      const dataByParameter = data.find(
        (timeSeriesResult) => timeSeriesResult.parameter.id === seriesConfig.parameter_id,
      );
      const presentationState = getChartSeriesPresentationState(dataByParameter, timeRange);
      const displayLabel = resolveSeriesDisplayLabel(seriesConfig, dataByParameter);
      const serieName = formatChartSeriesName(displayLabel, presentationState, t);
      const datapoints = dataByParameter?.data?.datapoints ?? [];
      const mappedDatapoints = datapoints.map((datapoint) => ({
        x: +new Date(datapoint.timestamp),
        y: datapoint.value,
      }));

      return {
        // base configuration
        type: "column",
        name: serieName,

        // line style configuration
        color: chartSeriesPresentationColor(
          presentationState,
          getColor(seriesConfig.color),
        ),
        enableMouseTracking: !chartSeriesIsDisabled(presentationState),
        tooltip: {
          pointFormatter: function (this) {
            return `${this.series.name}: <strong>${renderValue(
              this.y,
              config.unit,
            )}</strong>`;
          },
        },
        states: {
          hover: {
            lineWidthPlus: 0,
          },
        },

        // data configuration
        data: mappedDatapoints,
      };
    });
  }, [config, data, timeRange, t]);

  const thresholdPlotLines = useMemo(
    () => buildThresholdPlotLines(config.thresholds),
    [config.thresholds],
  );
  const thresholdPlotBands = useMemo(
    () => buildThresholdPlotBands(config.thresholds, data),
    [config.thresholds, data],
  );

  const yAxisPerUnit = getYAxisPerUnit(config.unit);
  const chartHeight = resolveGraphChartHeight(height);
  const graphOptions = useGraphOptions(
    {
      ...(chartHeight !== undefined ? { chart: { height: chartHeight } } : {}),
      title: {
        text: config.title,
      },
      yAxis: [
        { ...(yAxisPerUnit[0] ?? {}), plotLines: thresholdPlotLines },
        ...yAxisPerUnit.slice(1),
      ],
      xAxis: [
        {
          plotLines: [
            {
              id: "currentTime",
              color: "#01689B", // KNMI blue
              value: +new Date(currentTime.queryStringValue),
              zIndex: 4,
            },
          ],
          plotBands: thresholdPlotBands,
        },
      ],
      plotOptions: {
        column: {
          grouping: false,
          groupPadding: 0,
          // Columns grow from the axis threshold, so a value of 0 would render
          // a zero-height bar and read as missing data. A minimum length keeps
          // the bar visible at the baseline.
          minPointLength: 3,
        },
      },
      series: seriesWithData,
    },
    timeBounds,
  );

  const noData = useTimeSeriesNoData(config, data);

  return (
    <NoDataOverlay active={noData}>
      <HighchartsReact highcharts={Highcharts} options={graphOptions} />
    </NoDataOverlay>
  );
};

export default Histogram;
